import { WHATSAPP_NUMBER } from '../constants/homeData';
import { buildCourseBreadcrumbs } from './breadcrumbBuilders';
import { stripHtmlText } from './dashboardMappers';
import { formatDuration } from './formatDuration';


const DEFAULT_SUBSCRIPTION_FEATURES = [
  'Complete CHSE Syllabus Coverage',
  'Concept-Focused Video Lectures',
  'Exam-Oriented Important Questions',
  'Downloadable PDF Notes',
];

function getSubjectTagTextColor(colorCode) {
  if (!colorCode) return '#FFFFFF';
  const hex = colorCode.replace('#', '');
  if (hex.length !== 6) return '#FFFFFF';

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.65 ? '#1B1B1B' : '#FFFFFF';
}

function formatLessonDuration(minutes) {
  const total = Number(minutes) || 0;
  if (total <= 0) return null;
  return `${total} Min`;
}

function formatReviewDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return 'Today';
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }

  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function computeRatingDistribution(reviews = []) {
  const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach((review) => {
    const rating = Math.round(Number(review.rating) || 0);
    if (counts[rating] !== undefined) counts[rating] += 1;
  });

  const total = reviews.length || 1;

  return [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    percent: Math.round((counts[stars] / total) * 100),
  }));
}

function getLessonStatus(lesson, course, hasSubscriptionAccess) {
  const isSubscribed =
    hasSubscriptionAccess ??
    Boolean(course.is_subscription_taken);
  const hasAccess = !course.is_paid || isSubscribed || lesson.is_free_preview;
  if (!hasAccess) return 'locked';

  if (lesson.is_played_by_current_user || lesson.completed) return 'completed';
  if ((lesson.watched_seconds || 0) > 0) return 'in-progress';
  return 'available';
}

export function resolveLessonDisplayStatus(item, hasFullAccess) {
  if (item.type !== 'lecture') return item.status;
  if (item.status !== 'locked') return item.status;
  if (!hasFullAccess && !item.isFreePreview) return 'locked';
  if (item.isPlayed || item.completed) return 'completed';
  if ((item.watchedSeconds || 0) > 0) return 'in-progress';
  return 'available';
}

function mapLibraryFile(file) {
  const title = file.title || file.name || 'Resource';
  const lowerTitle = title.toLowerCase();
  const type = lowerTitle.includes('mcq') ? 'mcq' : 'notes';

  return {
    id: `file-${file.id}`,
    type,
    title,
    duration: null,
    status: 'resource',
    fileUrl: file.file_url || file.url || null,
  };
}

function mapLessonItem(lesson, course, hasSubscriptionAccess) {
  return {
    id: lesson.id,
    type: 'lecture',
    title: lesson.title,
    duration: formatLessonDuration(lesson.duration_minutes),
    status: getLessonStatus(lesson, course, hasSubscriptionAccess),
    isFreePreview: Boolean(lesson.is_free_preview),
    isPlayed: Boolean(lesson.is_played_by_current_user),
    completed: Boolean(lesson.completed),
    watchedSeconds: Number(lesson.watched_seconds) || 0,
    videoSource: lesson.video_source,
    youtubeUrl: lesson.youtube_url,
    vdocipherVideoId: lesson.vdocipher_video_id,
  };
}

function getChapterDurationMinutes(chapter) {
  const lessonMinutes = (chapter.lessons || []).reduce(
    (sum, lesson) => sum + (Number(lesson.duration_minutes) || 0),
    0
  );
  return lessonMinutes;
}

export function mapCourseChapters(chapters = [], course = {}, options = {}) {
  const hasSubscriptionAccess = options.hasSubscriptionAccess;

  return chapters.map((chapter, index) => {
    const lessons = chapter.lessons || [];
    const libraryFiles = chapter.library_files || [];
    const completedLessons = lessons.filter((lesson) => lesson.is_played_by_current_user).length;
    const totalLessons = lessons.length;
    const progressPercent =
      totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

    const items = [
      ...lessons.map((lesson) => mapLessonItem(lesson, course, hasSubscriptionAccess)),
      ...libraryFiles.map(mapLibraryFile),
    ];

    return {
      id: chapter.id,
      title: `Chapter ${chapter.position || index + 1} - ${chapter.title}`,
      lessonsCount: chapter.lessons_count ?? lessons.length,
      pdfCount: libraryFiles.length,
      duration: formatDuration(getChapterDurationMinutes(chapter)),
      progress: totalLessons > 0 ? { completed: completedLessons, total: totalLessons } : null,
      progressPercent,
      expanded: index === 0,
      items,
    };
  });
}

export function mapCourseReviews(reviewData) {
  const reviews = reviewData?.reviews || [];
  const average = Number(reviewData?.average_rating) || 0;
  const total = reviewData?.total_reviews ?? reviews.length;

  return {
    average: average || (reviews.length
      ? reviews.reduce((sum, item) => sum + (Number(item.rating) || 0), 0) / reviews.length
      : 0),
    totalRatings: total
      ? `${total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total} Rating & Review`
      : 'No ratings yet',
    distribution: computeRatingDistribution(reviews),
    items: reviews.map((review) => ({
      id: review.id,
      name: review.user_name || 'Student',
      avatar: review.user_profile_image_url || '',
      rating: Number(review.rating) || 0,
      timeAgo: formatReviewDate(review.created_at),
      text: review.comment || '',
    })),
  };
}

export function mapCourseDetail(course) {
  if (!course) return null;

  const descriptionText = stripHtmlText(course.description);
  const subtitleText = stripHtmlText(course.subtitle);
  const subjectName = course.subject_name || 'Course';
  const categoryName = course.category_name || course.category?.name || 'Courses';
  const benefits = (course.benefits || []).map((item) => item.title).filter(Boolean);
  const instructorImage = course.instructor_profile_image_url || "";

  return {
    id: course.id,
    popular: Boolean(course.popular),
    badge: course.popular
      ? 'Popular'
      : !course.is_paid
        ? 'Free Course'
        : null,
    title: course.title,
    breadcrumbs: buildCourseBreadcrumbs(course),
    description: subtitleText || descriptionText,
    rating: Number(course.rating) || 0,
    classTag: categoryName,
    subjectTag: subjectName,
    subjectColorCode: course.subject_color_code?.trim() || null,
    subjectTagStyle: course.subject_color_code
      ? { backgroundColor: course.subject_color_code }
      : undefined,
    subjectTagTextColor: getSubjectTagTextColor(course.subject_color_code),
    createdBy: 'CHSE Express',
    lastUpdated: formatReviewDate(course.updated_at) || course.updated_at || '—',
    heroImage: instructorImage || course.thumbnail_url,
    shareLink: course.share_link,
    isPaid: Boolean(course.is_paid),
    categoryId: course.category_id || course.category?.id || null,
    isSubscriptionTaken: Boolean(course.is_subscription_taken),
    instructor: {
      id: course.instructor_id,
      name: course.instructor_name || 'Instructor',
      role: `${subjectName} expert`.toLowerCase(),
      avatar: instructorImage,
      title: (course.instructor_title || subjectName).toUpperCase(),
      degree: course.instructor_qualification || '—',
      experience:
        course.instructor_experience_years != null && course.instructor_experience_years !== ''
          ? `${course.instructor_experience_years} yrs Experience`
          : '—',
      videos:
        course.instructor_number_of_videos != null && course.instructor_number_of_videos !== ''
          ? `${course.instructor_number_of_videos} Videos`
          : '—',
      bio: subtitleText || descriptionText.slice(0, 280),
      profileImage: instructorImage,
      subjectColorCode: course.subject_color_code?.trim() || null,
    },
    stats: {
      chapters: course.chapters_count || 0,
      lectures: course.lessons_count || 0,
      duration: formatDuration(course.duration_minutes),
    },
    courseDetails: descriptionText,
    whatYouLearn: benefits,
    subscription: {
      videoThumbnail: course.thumbnail_url,
      features: benefits.length ? benefits : DEFAULT_SUBSCRIPTION_FEATURES,
      phone: WHATSAPP_NUMBER,
      courseId: course.id,
      previewVideoUrl: course.youtube_url || null,
      previewVideoSource: course.video_source || null,
      vdocipherVideoId: course.vdocipher_video_id || null,
    },
    raw: course,
  };
}
